import { useCallback } from 'react';
import { useRegisterNewAccountByOtp as useRegisterNewAccountByOtpRequest } from '../api/requests';
import type { CustomerToken, RegisterAccountInfo } from '../types/front.graphql';
import { getDeviceInformation } from '@kidslabs/module-core';
import type { ResultData } from '../types';
import { authStore$ } from '../stores/auth.store';
import { ErrorCodesEnum } from '../enums';

export interface RegisterResultData extends ResultData {
    customerToken?: CustomerToken;
}

/**
 * Detects Magento "already registered" GraphQL errors.
 * Encapsulated here so consumers only check ErrorCodesEnum.CUSTOMER_REGISTERED.
 *
 * Error chain: Magento REST 400 → BadRequestException → GraphQL errors[].message
 * Known messages from Magento:
 * - "A customer with the same email address already exists in an associated website."
 * - "BadRequestException: A customer with the same email address already exists..."
 *
 * Also matches the Vietnamese message from CustomerRegisteredException:
 * - "Số điện thoại này đã được đăng ký"
 */
const isCustomerAlreadyRegisteredError = (message: string): boolean => {
    const lower = message.toLowerCase();
    return (
        lower.includes('already exists') || lower.includes('already registered') || lower.includes('đã được đăng ký')
    );
};

export const useRegisterNewAccountByOtp = () => {
    const { registerNewAccountByOtp: doRegisterNewAccountByOtp } = useRegisterNewAccountByOtpRequest();

    return useCallback(
        async (accountInfo: RegisterAccountInfo): Promise<RegisterResultData> => {
            const deviceInfo = getDeviceInformation();
            const response = await doRegisterNewAccountByOtp({
                variables: {
                    phoneNumber: authStore$.initValidatePhoneData.get()?.phoneNumber ?? '',
                    otp: authStore$.currentOtp.get() ?? '',
                    secret: authStore$.confirmRequestedValidationPhone.secret.get() ?? 'NO_SECRET',
                    accountInfo,
                    deviceRequest: deviceInfo,
                    referralCode: authStore$.initValidatePhoneData.get()?.referralCode,
                },
            });

            const customerToken = response?.data?.registerNewAccountByOtp?.customer_token;
            if (customerToken?.access_token) {
                return {
                    message: 'Register successful',
                    errorCode: ErrorCodesEnum.SUCCESS,
                    customerToken,
                };
            }

            const errorMessage = response?.errors?.map(e => e.message).join(', ') ?? 'Unable to register';

            // Detect "already registered" — Magento REST API returns HTTP 400,
            // which becomes BadRequestException in auth-service (magento-authentication.service.ts:56-59),
            // then re-thrown as GraphQL error in resolver (customer-creation.resolver.ts:195-201).
            // This is the ONLY place message matching happens; consumers use ErrorCodesEnum.
            if (isCustomerAlreadyRegisteredError(errorMessage)) {
                return { message: errorMessage, errorCode: ErrorCodesEnum.CUSTOMER_REGISTERED };
            }

            return { message: errorMessage, errorCode: ErrorCodesEnum.NOT_FOUND };
        },
        [doRegisterNewAccountByOtp]
    );
};
